import metas from '../../../locales/metas.json';
import {config} from '@modules/config';

export const SPOT_AI_PUBLIC_PREFIX = '/explore';
export const SPOT_AI_PUBLIC_SLUG = 'ai-music-detector';
export const NO_LANG_LOCALE = 'no-lang';

const PUBLIC_APP_ROOT = `${SPOT_AI_PUBLIC_PREFIX}/${SPOT_AI_PUBLIC_SLUG}`;

const activeLocaleSet = new Set(metas.sunsetLanguages);

// Keep these public URL rules in sync when Spot AI moves into deezer.com:
// canonical, hreflang, OG URLs, robots and sitemap must all share this shape.
export const ACTIVE_SEO_LOCALES = metas.availableLanguages.filter(
	(locale) => !activeLocaleSet.has(locale),
);

export const getDefaultSeoLocale = (): string => config.get('defaultLocale');

export const getSeoOrigin = (): string => {
	const host = config.get('host_website_ssl');
	return host.startsWith('http') ? host : `https://${host}`;
};

export const getLocaleDirection = (
	locale: string | undefined,
): 'ltr' | 'rtl' => {
	const direction =
		locale && locale in metas.languagesInfo
			? metas.languagesInfo[locale as keyof typeof metas.languagesInfo]
					.direction
			: 'ltr';

	return direction === 'rtl' ? 'rtl' : 'ltr';
};

export const normalizeAppPath = (path: string | undefined): string => {
	const rawPath = (path || '/').split(/[?#]/)[0] || '/';
	const withLeadingSlash = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
	const withoutTrailingSlash =
		withLeadingSlash.length > 1
			? withLeadingSlash.replace(/\/+$/, '')
			: withLeadingSlash;

	const publicRootPattern = new RegExp(
		`^${SPOT_AI_PUBLIC_PREFIX}(?:\\/[^/]+)?\\/${SPOT_AI_PUBLIC_SLUG}(?=\\/|$)`,
	);
	const appPath = withoutTrailingSlash.replace(publicRootPattern, '') || '/';

	return appPath.startsWith('/') ? appPath : `/${appPath}`;
};

export const buildPublicPath = ({
	locale,
	path,
}: {
	locale?: string;
	path?: string;
}): string => {
	const appPath = normalizeAppPath(path);
	const suffix = appPath === '/' ? '' : appPath;
	const defaultLocale = getDefaultSeoLocale();

	if (!locale || locale === defaultLocale) {
		return `${PUBLIC_APP_ROOT}${suffix}`;
	}

	return `${SPOT_AI_PUBLIC_PREFIX}/${locale}/${SPOT_AI_PUBLIC_SLUG}${suffix}`;
};

export const buildAbsolutePublicUrl = ({
	locale,
	path,
}: {
	locale?: string;
	path?: string;
}): string => `${getSeoOrigin()}${buildPublicPath({locale, path})}`;

export const buildAlternateLinks = (path?: string) => [
	{
		hrefLang: 'x-default',
		href: buildAbsolutePublicUrl({locale: NO_LANG_LOCALE, path}),
	},
	...ACTIVE_SEO_LOCALES.map((locale) => ({
		hrefLang: locale,
		href: buildAbsolutePublicUrl({locale, path}),
	})),
];

export const buildSitemapUrls = (paths: string[]): string[] =>
	paths.flatMap((path) =>
		ACTIVE_SEO_LOCALES.map((locale) => buildAbsolutePublicUrl({locale, path})),
	);
